/**
 * levels.js - Isaac No Youtube's five levels.
 *
 * CONTENT, NOT MECHANICS. All five sit in Phase 1, Echoic Foundation, which is
 * the whole of this game's coverage.
 *
 * ---------------------------------------------------------------------------
 * WHAT PHASE 1 GIVES A GAME, AND WHAT IT TAKES AWAY
 *
 * The framework's Phase 1 support bounds are `{ baseline: 0, maxSupport: 0,
 * minSupport: 0 }`. There is exactly ONE rung - `full_model` - so:
 *
 *   - every level's nominalRung is `full_model`, and it is not a choice;
 *   - support cannot fade and cannot be raised. `applyRecommendation()` clamps
 *     both directions to the same index, so the engine's two support actions
 *     are no-ops here. That is correct, not a defect: at Phase 1 the child is
 *     LEARNING that a voice does something, and there is nothing to fade to;
 *   - the within-trial ladder walk has nowhere to step, so every escalation is
 *     a `remodel` - Pip says the word again - and `buildLadderPlan()` produces
 *     that on its own. A game that invented an intermediate rung here would
 *     corrupt every Phase 1 judgement.
 *
 * So the only things that separate one level from the next are the ones the
 * spec's own level intents name: where the card is, how many cards there are,
 * and whether the words are new.
 *
 * ---------------------------------------------------------------------------
 * THE ARC, WHICH IS THE POINT OF THE GAME
 *
 * Isaac is drawn to a screen. Every level is somewhere further from it, and the
 * screen's own light is `glow` below - scenery, drawn and dimming, never a
 * target and never scored (see content/nearby.js).
 *
 *   L1  sofa       the screen is right there, and somebody sits down beside him
 *   L2  hall       somebody calls from a doorway; he has to look away to find them
 *   L3  doorstep   two of them are outside. Next door stays shut, and that is fine
 *   L4  garden     new words, and all of them are things you do WITH somebody
 *   L5  park       everybody, at once. The screen stayed at home
 *
 * `opening` is the level's own narration, said once when the level starts. It
 * lives beside the level rather than in voice.js because it is the arc, and an
 * arc split across two files gets edited out of order.
 *
 * ---------------------------------------------------------------------------
 * NO ROUND IS EVER THE SAME TWICE
 *
 * A level holds a POOL, and each round draws from it (../../shared/rounds.js),
 * avoiding what was just used and what was used in the last four rounds. A box
 * needs ten first attempts before it takes a colour and a level is replayed
 * until it has them, so a pool of four becomes a recital a child can predict.
 * Eight is the floor here, ten where three cards are on screen at once.
 */

/**
 * @typedef {object} Level
 * @property {string} id
 * @property {number} phase
 * @property {number} level
 * @property {string} scene              where Isaac is; purely presentational
 * @property {string} note               why the level is shaped this way
 * @property {string[]} opening          said once when the level starts
 * @property {'bright'|'fading'|'out'} glow  how much light the screen still has
 * @property {string[]} pool             nearby ids the rounds draw from
 * @property {number} rounds             rounds in one pass
 * @property {number} targetsPerRound    how many are named this round
 * @property {number} fieldSize      targets plus the rest of the field
 * @property {string} nominalRung        SUPPORT_LADDER id; `full_model` here
 * @property {'none'} cueStyle           Phase 1 never reaches a partial rung
 * @property {object} pacing             the within-trial walk, ms from the end
 *                                       of the prompt
 */

/** Default pacing. Per-level `pacing` overrides individual keys. */
export const DEFAULT_PACING = {
  hintAtMs: 5000,
  escalateAtMs: [8000],
  windowMs: 13000,
};

export const LEVELS = [
  {
    id: 'iny-p1l1',
    phase: 1,
    level: 1,
    scene: 'sofa',
    note: 'The spec\'s "1 object (center position)". One card, dead centre, and '
        + 'the screen still glowing at the edge. Nothing moves, because the only '
        + 'thing this level asks is that a voice makes something happen.',
    opening: [
      'Isaac is on the sofa. The tablet is still glowing.',
      'Somebody has sat down beside Isaac. Say who it is.',
    ],
    glow: 'bright',
    pool: ['mum', 'dad', 'nan', 'sam', 'baby', 'hug', 'teddy', 'uncle'],
    rounds: 3,
    targetsPerRound: 1,
    fieldSize: 1,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7000], windowMs: 12000 },
  },
  {
    id: 'iny-p1l2',
    phase: 1,
    level: 2,
    scene: 'hall',
    note: 'The spec\'s "1 object (different position)". Still one person to name, '
        + 'but they call from a doorway and the card arrives anywhere. The child '
        + 'has to look away from the light before they can name anybody, which is '
        + 'the only thing that changes from L1 and is the whole game in miniature.',
    opening: [
      'Isaac is in the hall. Somebody is calling from a doorway.',
      'Look away from the light and find who it is.',
    ],
    glow: 'bright',
    pool: ['dad', 'nan', 'ben', 'friend', 'auntie', 'tom', 'mum', 'hug'],
    rounds: 3,
    targetsPerRound: 1,
    fieldSize: 2,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7000], windowMs: 12000 },
  },
  {
    id: 'iny-p1l3',
    phase: 1,
    level: 3,
    scene: 'doorstep',
    note: 'The spec\'s "2 objects (sequential prompts)". Both are on the step from '
        + 'the start, prompted one after the other, so the child holds the second '
        + 'while answering the first. The neighbour\'s door is drawn shut and '
        + 'stays shut - it is never mentioned again, because a rule repeated is a '
        + 'rule a child learns to test.',
    opening: [
      'The front step. Two of them came out to wait for Isaac.',
      'Next door stays shut. Everybody Isaac needs is out here.',
    ],
    glow: 'fading',
    pool: ['mum', 'dad', 'nan', 'sam', 'ben', 'friend', 'auntie', 'uncle'],
    rounds: 2,
    targetsPerRound: 2,
    fieldSize: 3,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7500], windowMs: 13000 },
  },
  {
    id: 'iny-p1l4',
    phase: 1,
    level: 4,
    scene: 'garden',
    note: 'The spec\'s "2 objects (new vocabulary)". Every word here is new AND '
        + 'every one of them is something that needs another person in it - a '
        + 'song, a picnic, a swing. That is the content argument of the game '
        + 'stated as a pool rather than as a lecture.',
    opening: [
      'The garden. Now say the things you do together.',
      'Nobody does any of these on their own.',
    ],
    glow: 'fading',
    pool: ['park', 'bike', 'swing', 'song', 'garden', 'picnic', 'lunch', 'story'],
    rounds: 2,
    targetsPerRound: 2,
    fieldSize: 3,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [7500], windowMs: 13000 },
  },
  {
    id: 'iny-p1l5',
    phase: 1,
    level: 5,
    scene: 'park',
    note: 'The spec\'s "3 objects (more to remember)". People and doing-things in '
        + 'one pool, three at a time. The pool is ten rather than eight: three '
        + 'targets a round exhausts an eight-word pool inside two rounds and the '
        + 'four-round memory in shared/rounds.js then has nothing left to avoid.',
    opening: [
      'The park, and everybody came. The tablet stayed at home.',
      'Three at a time now. Say them all.',
    ],
    glow: 'out',
    pool: ['mum', 'dad', 'nan', 'friend', 'park', 'bike', 'swing', 'song', 'picnic', 'teddy'],
    rounds: 2,
    targetsPerRound: 3,
    fieldSize: 4,
    nominalRung: 'full_model',
    cueStyle: 'none',
    pacing: { hintAtMs: 5000, escalateAtMs: [8000], windowMs: 14000 },
  },
];

/**
 * What this game covers.
 *
 * Declared here, mirrored in content/index.js, and declared a THIRD time in
 * database/register_isaac-no-youtube.sql - which is the copy the SERVER reads,
 * deliberately: what a build covers is a fact about the build, not something a
 * client may assert. A conformance check asserts the three agree.
 */
export const COVERAGE = {
  phasesAvailable: [1],
  levelsAvailable: LEVELS.map((l) => ({ phase: l.phase, level: l.level })),
};

export function getLevelContent(phase, level) {
  return LEVELS.find((l) => l.phase === phase && l.level === level) || null;
}

export function pacingFor(level) {
  return { ...DEFAULT_PACING, ...(level.pacing || {}) };
}

export default { LEVELS, COVERAGE, DEFAULT_PACING, getLevelContent, pacingFor };
